// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

import { unzipSync } from 'fflate';
import { isSupported } from './extract.ts';

/*
 * Getting a folder of documentation into the tab, by whichever route the browser
 * allows. Every route ends at the same `collect()` result, so there is one
 * ingestion path and the sample button exercises it exactly like a real folder.
 */

export interface SourceFile {
  path: string;
  text: string;
}

export interface Ingested {
  files: SourceFile[];
  skipped: number;
  bytes: number;
}

const IGNORED_DIRS = new Set(['node_modules', '.git', 'dist', 'build', '.next', '.cache', 'vendor']);
const MAX_FILES = 5000;

function ignored(path: string): boolean {
  return path.split('/').some((seg) => IGNORED_DIRS.has(seg) || (seg.startsWith('.') && seg.length > 1));
}

export function supportsDirectoryPicker(): boolean {
  return typeof (window as unknown as { showDirectoryPicker?: unknown }).showDirectoryPicker === 'function';
}

async function readFile(file: File, path: string): Promise<SourceFile | null> {
  if (!isSupported(path) || ignored(path)) return null;
  return { path, text: await file.text() };
}

/** Expand a .zip in memory. The bundled sample arrives this way. */
export function readZip(bytes: Uint8Array): Ingested {
  const entries = unzipSync(bytes);
  const files: SourceFile[] = [];
  let skipped = 0;
  let total = 0;
  const dec = new TextDecoder();
  for (const [name, data] of Object.entries(entries)) {
    if (name.endsWith('/') || data.length === 0) continue;
    if (!isSupported(name) || ignored(name)) {
      skipped++;
      continue;
    }
    total += data.length;
    files.push({ path: name, text: dec.decode(data) });
  }
  return { files, skipped, bytes: total };
}

/** Files from an `<input type="file">`, including `webkitdirectory` selections. */
export async function fromFileList(list: FileList | File[]): Promise<Ingested> {
  const files: SourceFile[] = [];
  let skipped = 0;
  let bytes = 0;
  for (const file of Array.from(list)) {
    const path = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
    if (path.toLowerCase().endsWith('.zip')) {
      const zipped = readZip(new Uint8Array(await file.arrayBuffer()));
      files.push(...zipped.files);
      skipped += zipped.skipped;
      bytes += zipped.bytes;
      continue;
    }
    const read = await readFile(file, path);
    if (read) {
      files.push(read);
      bytes += file.size;
    } else {
      skipped++;
    }
    if (files.length >= MAX_FILES) break;
  }
  return { files, skipped, bytes };
}

interface DirectoryHandle {
  kind: 'directory';
  name: string;
  values(): AsyncIterable<DirectoryHandle | FileHandleLike>;
}

interface FileHandleLike {
  kind: 'file';
  name: string;
  getFile(): Promise<File>;
}

async function walkHandle(handle: DirectoryHandle, prefix: string, out: Ingested): Promise<void> {
  if (out.files.length >= MAX_FILES) return;
  for await (const entry of handle.values()) {
    const path = prefix ? `${prefix}/${entry.name}` : entry.name;
    if (entry.kind === 'directory') {
      if (IGNORED_DIRS.has(entry.name) || (entry.name.startsWith('.') && entry.name.length > 1)) continue;
      await walkHandle(entry, path, out);
    } else {
      const file = await entry.getFile();
      const read = await readFile(file, path);
      if (read) {
        out.files.push(read);
        out.bytes += file.size;
      } else {
        out.skipped++;
      }
    }
    if (out.files.length >= MAX_FILES) return;
  }
}

/** File System Access directory picker (Chromium desktop). */
export async function fromDirectoryPicker(): Promise<Ingested> {
  const picker = (window as unknown as { showDirectoryPicker: () => Promise<DirectoryHandle> })
    .showDirectoryPicker;
  const handle = await picker();
  const out: Ingested = { files: [], skipped: 0, bytes: 0 };
  await walkHandle(handle, '', out);
  return out;
}

interface FileSystemEntryLike {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  fullPath: string;
  file(cb: (f: File) => void, err: (e: unknown) => void): void;
  createReader(): { readEntries(cb: (entries: FileSystemEntryLike[]) => void, err: (e: unknown) => void): void };
}

function readEntries(
  reader: ReturnType<FileSystemEntryLike['createReader']>,
): Promise<FileSystemEntryLike[]> {
  return new Promise((resolve, reject) => reader.readEntries(resolve, reject));
}

async function walkEntry(entry: FileSystemEntryLike, prefix: string, out: Ingested): Promise<void> {
  if (out.files.length >= MAX_FILES) return;
  const path = prefix ? `${prefix}/${entry.name}` : entry.name;
  if (entry.isDirectory) {
    if (IGNORED_DIRS.has(entry.name) || (entry.name.startsWith('.') && entry.name.length > 1)) return;
    const reader = entry.createReader();
    for (;;) {
      // readEntries returns at most 100 per call and must be drained.
      const batch = await readEntries(reader);
      if (batch.length === 0) break;
      for (const child of batch) await walkEntry(child, path, out);
    }
    return;
  }
  const file = await new Promise<File>((resolve, reject) => entry.file(resolve, reject));
  if (file.name.toLowerCase().endsWith('.zip')) {
    const zipped = readZip(new Uint8Array(await file.arrayBuffer()));
    out.files.push(...zipped.files);
    out.skipped += zipped.skipped;
    out.bytes += zipped.bytes;
    return;
  }
  const read = await readFile(file, path);
  if (read) {
    out.files.push(read);
    out.bytes += file.size;
  } else {
    out.skipped++;
  }
}

/** A dropped folder or set of files. */
export async function fromDataTransfer(dt: DataTransfer): Promise<Ingested> {
  const out: Ingested = { files: [], skipped: 0, bytes: 0 };
  const items = Array.from(dt.items ?? []).filter((i) => i.kind === 'file');

  const entries = items
    .map((item) => {
      // The DOM lib types this as FileSystemEntry, which lacks the recursive
      // reader shape we actually use; go through unknown deliberately.
      const get = (item as DataTransferItem).webkitGetAsEntry;
      if (typeof get !== 'function') return null;
      return (get.call(item) as unknown as FileSystemEntryLike | null) ?? null;
    })
    .filter((e): e is FileSystemEntryLike => e !== null);

  if (entries.length > 0) {
    for (const entry of entries) await walkEntry(entry, '', out);
    return out;
  }
  return fromFileList(dt.files);
}

/** Fetch the bundled sample and hand it to the identical ingestion path. */
export async function fromSample(url: string): Promise<Ingested> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Could not load the sample (HTTP ${res.status})`);
  return readZip(new Uint8Array(await res.arrayBuffer()));
}

/** Longest common directory prefix — used to suggest a "strip prefix" value. */
export function commonPrefix(paths: string[]): string {
  if (paths.length === 0) return '';
  const split = paths.map((p) => p.split('/').slice(0, -1));
  let prefix = split[0];
  for (const parts of split.slice(1)) {
    let i = 0;
    while (i < prefix.length && i < parts.length && prefix[i] === parts[i]) i++;
    prefix = prefix.slice(0, i);
    if (prefix.length === 0) break;
  }
  return prefix.length > 0 ? `${prefix.join('/')}/` : '';
}
